/**
 *
 * @module component/extender/extend
 * @author Adam Pirani [adam.pirani@mlb.com]
 * @description Extender 'extend' helpers
 *
 */

import helpers from './../helpers';
const { i18n } = helpers;
import fill from 'lodash-es/fill';
import get from 'lodash-es/get';
import extenderGet from './get';
import MlbTbdTeamToLeagueMap from 'mlb-tbd-team-to-league-map/dist/index.bundle';

const MODES_WITH_TEAM_LINKS = {
    responsive: true,
    MIG: true
};

const MLB_SPORT_ID = 1;
const INTERNATIONAL_SPORT_ID = 51;
const JAPAN_ALL_STAR_SERIES_ID = 420;
const WINTER_LEAGUE_ID = 17; // AFL
const LINESCORE_NUM_INNING_WIDTH = 10;
const DEFAULT_SCHEDULED_INNINGS = 9;

/**
 *
 * @function extendTeams
 * @param {MlbLinker} mlbLinker
 * @param {object} [teams]
 * @param {boolean} isFinal
 * @returns {Object} extended data
 *
 */
function extendTeams(opts = {}, teams = {}, isFinal = false) {

    const { mlbLinker, mode = 'responsive', teamId, milbSportIds = '' } = opts;
    let { home, away } = teams;
    const homeSport = get(home, 'team.sport.id');
    const awaySport = get(away, 'team.sport.id');
    const homeId = get(home, 'team.id');
    const awayId = get(away, 'team.id');
    const homeLeagueId = get(home, 'team.league.id');
    const awayLeagueId = get(away, 'team.league.id');

    home.isMLB = (homeSport === MLB_SPORT_ID);
    away.isMLB = (awaySport === MLB_SPORT_ID);
    home.isInternational = (homeSport === INTERNATIONAL_SPORT_ID);
    away.isInternational = (awaySport === INTERNATIONAL_SPORT_ID);
    home.isWinterLeague = (homeSport === WINTER_LEAGUE_ID);
    away.isWinterLeague = (awaySport === WINTER_LEAGUE_ID);

    home.isMiLB = (milbSportIds.indexOf(homeSport) !== -1);
    away.isMiLB = (milbSportIds.indexOf(awaySport) !== -1);

    home.isTBDTeam = home && home.team && home.team.placeholder || MlbTbdTeamToLeagueMap.isTbdTeam(homeId);
    away.isTBDTeam = away && away.team && away.team.placeholder || MlbTbdTeamToLeagueMap.isTbdTeam(awayId);
    home.logoId = getLogoId(home, homeId);
    away.logoId = getLogoId(away, awayId);

    function getLogoId(team, teamId) {
        const displayLogo = (team.isMLB || team.isMiLB || team.isInternational || team.isWinterLeague) && !team.isTBDTeam;
        return displayLogo ? teamId : MlbTbdTeamToLeagueMap.getLeagueIdFromTbdTeamId(teamId) || '000';
    }

    if (isFinal) {

        if (home.score < away.score) {

            home.isLoser = true;

        } else if (home.score > away.score) {

            away.isLoser = true;

        }
    }

    if (MODES_WITH_TEAM_LINKS[mode] && mlbLinker) {

        let linkHomeTeam = home.isMLB;
        let linkAwayTeam = away.isMLB;

        if (teamId) {

            if (teamId === homeId) {

                linkHomeTeam = false;

            } else if (teamId === awayId) {

                linkAwayTeam = false;

            }

        }

        home.url = linkHomeTeam && mlbLinker.getTeamHomepageLink(home.team) || '';
        away.url = linkAwayTeam && mlbLinker.getTeamHomepageLink(away.team) || '';

    }

    return teams;

}

/**
 * @function extendLinescore
 * @description extends the linescore data.
 * @param {object} gameData
 * @param {object} statusAbstract
 * @returns {object}
 */
function extendLinescore(gameData = {}, statusAbstract = {}) {

    const { linescore = {}, sport = '' } = gameData;

    const { innings = [], scheduledInnings = DEFAULT_SCHEDULED_INNINGS } = linescore;
    const { isFinal, isLive, isCompletedEarly } = statusAbstract;

    const missingDataForHitsErrors = get(linescore, 'teams.away.errors') === undefined;
    const numInnings = innings.length;
    const extraInnings = numInnings > DEFAULT_SCHEDULED_INNINGS;
    let inningLabels = [];
    let inningsAway = [];
    let inningsHome = [];

    if (missingDataForHitsErrors) {

        fill(inningLabels, '', 0, LINESCORE_NUM_INNING_WIDTH);

    } else {

        for (let i = 0, len = Math.max(LINESCORE_NUM_INNING_WIDTH, numInnings); i < len; i = i + 1) {

            const inning = innings[i];
            let inningLabel = i + 1;

            if (inningLabel === LINESCORE_NUM_INNING_WIDTH && !inning) {

                inningLabel = '';
            }

            inningLabels.push(inningLabel);

            if (inning) {

                const runsAway = get(inning, 'away.runs');
                const runsHome = get(inning, 'home.runs');

                if (runsAway !== undefined) {

                    inningsAway.push({
                        runs: runsAway,
                        isCurrent: false
                    });
                }

                if (runsHome !== undefined) {

                    inningsHome.push({
                        runs: runsHome,
                        isCurrent: false
                    });
                }
            }
        }
    }

    if (isLive) {

        //Shade current half inning
        const currentInning = get(linescore, 'currentInning', 0);
        const inningState = get(linescore, 'inningState', '');

        if (currentInning) {

            if (inningState === 'Top' || inningState === 'Bottom') {

                const inningsHomeAway = inningState === 'Top' ? inningsAway : inningsHome;

                if (!inningsHomeAway[currentInning - 1]) {

                    inningsHomeAway.push({
                        runs: '',
                        isCurrent: true
                    });

                } else {

                    inningsHomeAway[currentInning - 1].isCurrent = true;

                }

            }

        }

    } else if (isFinal && (inningsHome.length < scheduledInnings || isCompletedEarly)) {

        let padding = missingDataForHitsErrors ? ' ' : 'X';

        // Fill unplayed innings of final games with 'X's
        const fillerInning = {
            runs: padding,
            isCurrent: false
        };
        const unplayedHomeInnings = scheduledInnings - inningsHome.length;
        const unplayedAwayInnings = scheduledInnings - inningsAway.length;

        if (unplayedHomeInnings > 0) {
            inningsHome = inningsHome.concat(
                fill(new Array(unplayedHomeInnings), fillerInning)
            );
        }
        if (unplayedAwayInnings > 0) {
            inningsAway = inningsAway.concat(
                fill(new Array(unplayedAwayInnings), fillerInning)
            );
        }

    }

    // MiLB only shows final 9 innings
    const currentInning = get(linescore, 'currentInning', 0);
    if (currentInning > 9 && sport === 'milb') {
        inningLabels = inningLabels.slice(inningLabels.length - 9);
        inningsAway = inningsAway.slice(inningsAway.length - 9);
        inningsHome = inningsHome.slice(inningsHome.length - 9);
    }

    linescore.inningLabels = inningLabels;
    linescore.inningsAway = inningsAway;
    linescore.inningsHome = inningsHome;

    if (missingDataForHitsErrors) {

        linescore.teams = linescore.teams || {};
        linescore.teams.away = linescore.teams.away || {};
        linescore.teams.away.runs = get(gameData, 'teams.away.score', '');

        linescore.teams = linescore.teams || {};
        linescore.teams.home = linescore.teams.home || {};
        linescore.teams.home.runs = get(gameData, 'teams.home.score', '');

    }

    linescore.headerLabels = {
        runs: i18n('stats_runs_abbrev', 'R'),
        hits: missingDataForHitsErrors ? '' : i18n('stats_hits_abbrev', 'H'),
        errors: missingDataForHitsErrors ? '' : i18n('stats_errors_abbrev', 'E')
    };

    linescore.extraInnings = extraInnings;

    return linescore;

}

/**
 * @function extendMlbButtons
 * @description if minisb, know which buttons to hide in horizontal view
 * @param mode
 * @param statusAbstract
 * @param mlbButtons MlbButtonGroup
 * @returns {Array}
 */
function extendMlbButtons(mode = '', statusAbstract = {}, mlbButtons = {}, flag = {}, device = 'desktop') {

    if (mode === 'minisb') {

        if (statusAbstract.isPreview) {

            if (mlbButtons.hasOwnProperty('watch') && mlbButtons.tickets) {

                mlbButtons.tickets.hideInMiniSm = true;

            }

            // TODO: Revert once we have hover for touch devices
            if (device !== 'desktop' && mlbButtons.tickets) {

                mlbButtons.tickets.hideInMiniSm = true;

            }

        }

        if (statusAbstract.isLive) {

            if (mlbButtons.audio) {

                mlbButtons.audio.hideInMiniSm = true;

            }

            if (flag.type === 'live-lookin' && mlbButtons.hasOwnProperty('watch')) {

                mlbButtons.watch.hideInMiniSm = true;

            }

        }

        if (statusAbstract.isFinal) {

            if (mlbButtons.vidhighlights) {

                mlbButtons.vidhighlights.hideInMiniSm = true;

            }

            // TODO: Revert once we have hover for touch devices
            if (device !== 'desktop' && mlbButtons.wrap) {

                mlbButtons.wrap.hideInMiniSm = true;

            }

        }

    }

    let buttons = mlbButtons.getButtonArray();
    buttons.forEach(button => {

        if (mode === 'minisb') {

            button.textAsTooltip = true;

        }

        let buttonType = get(button, 'supplementaryData.buttonType', '');
        button.className = 'p-button--scores-' + buttonType;
    });

    return buttons;

}

/**
 *
 * @param status
 * @returns {{abstract: {isFinal: boolean, isLive: boolean, isPreview: boolean}}}
 */
function extendStatus(gameData = {}, opts = {}) {

    let { gameUtils, isPostseasonPush, showDescription = '' } = opts;
    let { gameType, tiebreaker, doubleHeader = 'N' } = gameData;
    let isFreeGame = get(gameData, 'content.media.freeGame', false);
    let status = get(gameData, 'status', {});
    let extStatus = gameUtils.getExtendedStatus(gameData);
    let { getGameOfTheWeek } = extenderGet;
    let isFacebookOfTheWeek = getGameOfTheWeek('facebook', gameData);

    let { abstractGameCode, codedGameState, statusCode, startTimeTBD } = status;
    let isInGameDelay = (codedGameState === 'I' && (statusCode !== 'I' && statusCode !== 'IH'));
    let isPreGameDelay = (codedGameState === 'P' && (statusCode !== 'P' && statusCode !== 'PW'));
    let completedEarly = (codedGameState === 'F' && (statusCode !== 'F' && statusCode !== 'FT' && statusCode !== 'FW'));

    return Object.assign(status, {
        abstract: {
            isFinal: abstractGameCode === 'F',
            isLive: abstractGameCode === 'L',
            isPreview: abstractGameCode === 'P',
            isCancelled: codedGameState === 'C',
            isWarmup: statusCode === 'PW',
            isPostponed: codedGameState === 'D',
            isDelayed: (isInGameDelay || isPreGameDelay),
            isInstantReplay: statusCode === 'IH',
            isManagerChallenge: codedGameState === 'M',
            isUmpireReview: codedGameState === 'N',
            isSuspended: (codedGameState === 'T' || codedGameState === 'U'),
            isCompletedEarly: completedEarly,
            isFreeGame: isFreeGame,
            isShowDescription: showDescription === 'Y',
            isAllStar: gameType === 'A',
            isExhibition: gameType === 'E',
            isSpring: gameType === 'S',
            isTBD: startTimeTBD,
            isClassicDoubleHeader: doubleHeader === 'Y',
            isSplitTicketDoubleHeader: doubleHeader === 'S',
            isDoubleHeader: doubleHeader !== 'N',
            isPostSeasonReady: extStatus.isPostSeasonReady,
            isDelayedOrSuspended: extStatus.isDelayed || extStatus.isSuspended,
            isFacebookOfTheWeek: isFacebookOfTheWeek,
            isPostseasonPush: isPostseasonPush
        }
    });
}

export default {
    extendLinescore,
    extendMlbButtons,
    extendStatus,
    extendTeams
};
